<template>
  <div class="hkl-header">
    <div class="hkl-header-left">
      <a
        class="hkl-header-back"
        :class="linkClassLeftPrimary"
        href="#"
        @click.prevent="onLeftPrimaryClick"
      >
        &lt; {{ labelLeftPrimary }}
      </a>
      <a
        v-if="labelLeftSecondary"
        class="hkl-header-link"
        href="#"
        @click.prevent="onLeftSecondaryClick"
      >
        &lt; {{ labelLeftSecondary }}
      </a>
    </div>
    <div
      v-if="showRightCluster"
      class="hkl-header-right"
    >
      <a
        v-if="labelRightLeading"
        class="hkl-header-link"
        :class="{ 'hkl-header-link--disabled': shouldDisablePrev }"
        href="#"
        @click.prevent="onRightLeadingClick"
      >
        &lt; {{ labelRightLeading }}
      </a>
      <a
        v-if="labelRightTrailing"
        class="hkl-header-link"
        :class="{ 'hkl-header-link--disabled': shouldDisableNext }"
        href="#"
        @click.prevent="onRightTrailingClick"
      >
        {{ labelRightTrailing }} &gt;
      </a>
    </div>
  </div>
</template>

<script>
import {
  LIST_NAV_KIND,
  getListNavigationEntryRouteName,
  resolveNavigationKind,
} from '@/constants/listNavigation'

export default {
  name: 'HKLHeader',
  props: {
    labelLeftPrimary: {
      type: String,
      default: '',
    },
    labelLeftSecondary: {
      type: String,
      default: '',
    },
    labelRightLeading: {
      type: String,
      default: '',
    },
    labelRightTrailing: {
      type: String,
      default: '',
    },
    linkClassLeftPrimary: {
      type: [String, Object, Array],
      default: '',
    },
    /**
     * Ordered list rows for Prev/Next. Header only emits; parent navigates via
     * `pushListNavigationEntry(router, route, mergeListNavigationHeaderPaths(navPathsByKind, navBasePathHotb, navBasePathTenantProject), entry)`.
     */
    navigationEntries: {
      type: Array,
      default: () => [],
    },
    /** Passed through to mergeListNavigationHeaderPaths (HOTB path prefix before `:id`). */
    navBasePathHotb: {
      type: String,
      default: '',
    },
    /** Passed through to mergeListNavigationHeaderPaths (tenant-project path prefix before `:id`). */
    navBasePathTenantProject: {
      type: String,
      default: '',
    },
    /**
     * Extra kind → path prefix for non–tenant-project flows (e.g. `{ tender: '/tenders' }`).
     * Keys must match `kind` values committed to the listNavigation store.
     */
    navPathsByKind: {
      type: Object,
      default: () => ({}),
    },
    /** Defaults to `$route.params.id` when omitted */
    currentRecordId: {
      type: [String, Number],
      default: null,
    },
    /** When set, overrides list-index prev disabled state (e.g. batch review uses list nav for Previous only). */
    navigatePrevEnabled: {
      type: Boolean,
      default: undefined,
    },
    /** When set, overrides list-index next disabled state (e.g. batch review uses review queue for Next). */
    navigateNextEnabled: {
      type: Boolean,
      default: undefined,
    },
  },
  computed: {
    resolvedCurrentRecordId () {
      if (this.currentRecordId !== null && this.currentRecordId !== undefined && this.currentRecordId !== '') {
        return String(this.currentRecordId)
      }
      const id = this.$route && this.$route.params && this.$route.params.id
      return id != null ? String(id) : ''
    },
    normalizedNavigationEntries () {
      return (this.navigationEntries || [])
        .filter(e => e && e.id != null && e.id !== '')
        .map(e => {
          const kind = resolveNavigationKind(e)
          const rn = getListNavigationEntryRouteName(e)
          return {
            id: e.id,
            kind,
            routeName: rn || undefined,
            params: e.params && typeof e.params === 'object' ? { ...e.params } : undefined,
            query: e.query && typeof e.query === 'object' ? { ...e.query } : undefined,
            isHotb: kind === LIST_NAV_KIND.HOTB,
          }
        })
    },
    recordIndex () {
      return this.normalizedNavigationEntries.findIndex(e => String(e.id) === this.resolvedCurrentRecordId)
    },
    prevEntry () {
      const i = this.recordIndex
      if (i <= 0) return null
      return this.normalizedNavigationEntries[i - 1]
    },
    nextEntry () {
      const i = this.recordIndex
      const list = this.normalizedNavigationEntries
      if (i < 0 || i >= list.length - 1) return null
      return list[i + 1]
    },
    showRightCluster () {
      return this.normalizedNavigationEntries.length > 0
    },
    useRecordNavigation () {
      return !!(this.showRightCluster && this.resolvedCurrentRecordId && this.recordIndex >= 0)
    },
    canNavigatePrev () {
      if (typeof this.navigatePrevEnabled === 'boolean') return this.navigatePrevEnabled
      if (!this.useRecordNavigation) return true
      return this.recordIndex > 0
    },
    canNavigateNext () {
      if (typeof this.navigateNextEnabled === 'boolean') return this.navigateNextEnabled
      if (!this.useRecordNavigation) return true
      const list = this.normalizedNavigationEntries
      return this.recordIndex >= 0 && this.recordIndex < list.length - 1
    },
    shouldDisablePrev () {
      if (typeof this.navigatePrevEnabled === 'boolean') return !this.navigatePrevEnabled
      return this.useRecordNavigation && !this.canNavigatePrev
    },
    shouldDisableNext () {
      if (typeof this.navigateNextEnabled === 'boolean') return !this.navigateNextEnabled
      return this.useRecordNavigation && !this.canNavigateNext
    },
  },
  methods: {
    emitNavPayload (entry) {
      if (!entry) return null
      return {
        id: entry.id,
        kind: entry.kind,
        routeName: entry.routeName,
        params: entry.params,
        query: entry.query,
        isHotb: entry.isHotb,
      }
    },
    /** Parent must listen (e.g. `goBack` or return to search results); no built-in fallback. */
    onLeftPrimaryClick () {
      this.$emit('left-primary')
    },
    onLeftSecondaryClick () {
      this.$emit('left-secondary')
    },
    /** Parent should call `pushListNavigationEntry` from `@/constants/listNavigation`; payload matches `emitNavPayload(prevEntry)`. */
    onRightLeadingClick () {
      if (this.shouldDisablePrev) return
      this.$emit('right-leading', this.emitNavPayload(this.prevEntry))
    },
    onRightTrailingClick () {
      if (this.shouldDisableNext) return
      this.$emit('right-trailing', this.emitNavPayload(this.nextEntry))
    },
  },
}
</script>

<style lang="scss" scoped>
.hkl-header-back,
.hkl-header-link {
  display: inline-block;
  margin-top: 5px;
  margin-bottom: 5px;
  font-family: Verdana;
  font-size: 14px;
  color: #0c3a58;
  cursor: pointer;
  text-decoration: none;
}

.hkl-header-link--disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
}

.hkl-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.hkl-header-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.hkl-header-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;

  .hkl-header-link + .hkl-header-link {
    padding-left: 24px;
    padding-right: 4px;
  }
}
</style>
